import { ReactElement, useEffect, useMemo, useState } from 'react'
import { useController, useFormContext } from 'react-hook-form'
import { NumberFormatValues, SourceInfo } from 'react-number-format'
import Decimal from 'decimal.js'
import { useDebouncedCallback } from 'use-debounce'
import {
  TextField as MuiTextField,
  InputProps,
  OutlinedTextFieldProps,
  InputAdornment,
} from '@mui/material'
import FormControl from '../FormControl'
import NumberInput from '../NumberInput'
import { newValueOnChange, snapWithDecimal } from '../RelativePositionField'

interface Props extends Omit<OutlinedTextFieldProps, 'variant' | 'type'> {
  name: string
  placeholder?: string
  label?: string
  inputLabel?: string
  tooltip?: TooltipContents
  InputProps?: InputProps
  length: number
  snapValues?: SnapValues[]
  onValueChange?: (relativePosition: string) => void
  'data-cy'?: string
}

/**
 * Like `RelativePositionField`, but it keeps the form value exact: only what the user types is
 * written back. The number input also reports a change when the displayed value changes from
 * outside (a form reset, another length), already rounded to two decimals; writing that back (as
 * `RelativePositionField` does) replaces an exact decimal position with a rounded one and makes a
 * just reset form dirty again.
 */
const RelativePositionFieldV2 = ({
  name,
  rows = 1,
  multiline = false,
  autoFocus = false,
  placeholder = '',
  disabled = false,
  label = '',
  inputLabel,
  InputProps = {},
  inputProps,
  snapValues,
  tooltip,
  length,
  onValueChange,
  'data-cy': dataCy,
  ...textFieldProps
}: Props): ReactElement => {
  const { control } = useFormContext()
  const {
    field: { onChange, onBlur, name: fieldName, value, ref },
    fieldState: { error },
  } = useController({ name, control })
  // internal value is stored as a string
  const [internalValue, setInternalValue] = useState(value)
  const lengthAsDecimal = new Decimal(length)

  const onChangeDebounced = useDebouncedCallback(
    (valueAsDecimal: Decimal) => {
      const correctedValueAsDecimal = snapWithDecimal(valueAsDecimal, lengthAsDecimal, snapValues)
      const correctedValueAsString = correctedValueAsDecimal.toString()
      onChange(correctedValueAsString)
      setInternalValue(correctedValueAsString)
      onValueChange?.(correctedValueAsString)
    },
    snapValues ? 500 : 100,
  )

  // The form value only changes from outside (reset, setValue) or by the debounced write above.
  // A change from outside wins over input still waiting in the debounce, which would otherwise
  // overwrite e.g. a reset that happens right after typing.
  useEffect(() => {
    onChangeDebounced.cancel()
    setInternalValue(value)
  }, [value, onChangeDebounced])

  const displayValue = useMemo(() => {
    return !internalValue ? internalValue : Number(internalValue) * length
  }, [internalValue, length])

  const handleNumberInputValueChange = (values: NumberFormatValues, sourceInfo: SourceInfo) => {
    // A change of the displayed value from outside is not an edit
    if (sourceInfo.source !== 'event') return

    // consider case that the text field is empty
    const newValueAsString = newValueOnChange(values.value, lengthAsDecimal)
    if (newValueAsString.length) {
      setInternalValue(newValueAsString)
      onChangeDebounced(new Decimal(newValueAsString))
    }
  }

  return (
    <FormControl
      data-cy={dataCy}
      label={!inputLabel ? label : undefined}
      error={error?.message}
      tooltip={tooltip}
    >
      <MuiTextField
        variant="outlined"
        inputProps={{
          step: 0.1,
          ...inputProps,
          // Last, so no caller can replace it and bring back the rounded write-back
          onValueChange: handleNumberInputValueChange,
        }}
        onBlur={onBlur}
        value={displayValue}
        type="text"
        placeholder={placeholder}
        autoFocus={autoFocus}
        name={fieldName}
        disabled={disabled}
        multiline={multiline}
        rows={rows}
        inputRef={ref}
        size="small"
        InputProps={{
          inputComponent: NumberInput as never,
          endAdornment: <InputAdornment position="end">m</InputAdornment>,
          ...InputProps,
        }}
        label={inputLabel}
        data-cy={dataCy}
        {...textFieldProps}
      />
    </FormControl>
  )
}

export { RelativePositionFieldV2 }
